import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import useBranches from "@/hooks/useBranches";
import { Plus, PencilSimple, Trash, X, IdentificationCard } from "@phosphor-icons/react";

/**
 * Service staff master — Advisors + Technicians. Mirrors SalesAgentsPage
 * for consistency; branch-scoped and admin-managed. One `service_staff`
 * collection on the backend, discriminated by `role`.
 */
const ROLE_TABS = [
  { id: "advisor", label: "Service Advisors" },
  { id: "technician", label: "Service Technicians" },
];

const BLANK = (role) => ({
  name: "",
  role,
  phone: "",
  email: "",
  code: "",
  branch_id: "",
  active: true,
});

function StaffFormDrawer({ open, editing, role, branches, onClose, onSaved }) {
  const [form, setForm] = useState(BLANK(role));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? { ...BLANK(role), ...editing } : BLANK(role));
  }, [open, editing, role]);

  if (!open) return null;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.branch_id) {
      toast.error("Branch is required — service staff are tied to a specific branch.");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form };
      if (editing) await api.patch(`/service-staff/${editing.id}`, body);
      else await api.post("/service-staff", body);
      toast.success(editing ? "Staff updated" : "Staff created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = role === "advisor" ? "Service Advisor" : "Service Technician";

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        data-testid="staff-form-drawer"
        className="w-[440px] bg-canvas-card border-l-2 border-ink overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
              {editing ? `Edit ${roleLabel}` : `New ${roleLabel}`}
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1">
              {editing ? editing.name : roleLabel}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 border border-canvas-line hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {[
            ["Name", "name", true, ""],
            ["Phone", "phone", false, ""],
            ["Email", "email", false, ""],
            ["Employee Code", "code", false, "e.g., TECH-001"],
          ].map(([label, key, required, placeholder]) => (
            <div key={key}>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                {label}
                {required && <span className="text-destructive ml-1">*</span>}
              </label>
              <input
                value={form[key] || ""}
                onChange={(e) => set(key)(e.target.value)}
                required={required}
                placeholder={placeholder}
                data-testid={`staff-${key}`}
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
            </div>
          ))}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Branch <span className="text-destructive">*</span>
            </label>
            <select
              value={form.branch_id || ""}
              onChange={(e) => set("branch_id")(e.target.value)}
              data-testid="staff-branch"
              required
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="">— Select a branch —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <div className="font-mono text-[10px] text-ink-subtle mt-1">
              Only appears in the service invoice picker for this branch.
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.active !== false}
              onChange={(e) => set("active")(e.target.checked)}
              data-testid="staff-active"
              className="accent-ink"
            />
            <span className="font-mono text-xs uppercase tracking-wider text-ink-muted">
              Active (can be selected on new invoices)
            </span>
          </label>
          <div className="pt-4 border-t border-canvas-line flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="staff-save"
              className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Save" : `Create ${roleLabel.toLowerCase()}`}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function ServiceStaffPage() {
  const [role, setRole] = useState("advisor");
  const [rows, setRows] = useState([]);
  const { branches } = useBranches();
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/service-staff", { params: { role } });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const branchById = useMemo(
    () => Object.fromEntries((branches || []).map((b) => [b.id, b])),
    [branches],
  );

  const roleLabel = role === "advisor" ? "Service Advisor" : "Service Technician";

  const onDelete = async (staff) => {
    if (!window.confirm(`Delete ${staff.name}?`)) return;
    try {
      const { data } = await api.delete(`/service-staff/${staff.id}`);
      toast.success(data.deactivated
        ? `Marked inactive (used by ${data.in_use} invoice${data.in_use === 1 ? "" : "s"})`
        : "Staff deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="service-staff-page">
      <PageHeader
        eyebrow="13 / Service"
        title="Service Staff"
        subtitle="Advisors and technicians whose names appear on job cards & service invoices. Independent of system users, tied to a branch."
        actions={
          <button
            onClick={() => { setEditing(null); setDrawerOpen(true); }}
            data-testid="add-staff-button"
            className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
          >
            <Plus size={14} weight="bold" />
            New {roleLabel}
          </button>
        }
      />

      <div className="px-8 py-6 space-y-4">
        <div className="inline-flex border border-canvas-line" data-testid="staff-role-tabs">
          {ROLE_TABS.map((t, i) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setRole(t.id)}
              data-testid={`staff-tab-${t.id}`}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider ${
                i > 0 ? "border-l border-canvas-line" : ""
              } ${role === t.id ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="staff-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  {["Name", "Code", "Phone", "Email", "Branch", "Status", ""].map((h) => (
                    <th
                      key={h || "act"}
                      className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                    >{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <IdentificationCard size={24} weight="bold" className="text-ink-subtle mx-auto mb-2" />
                      <div className="font-mono text-xs text-ink-subtle">
                        No {roleLabel.toLowerCase()}s yet. Add the first one to start attributing service invoices.
                      </div>
                    </td>
                  </tr>
                )}
                {rows.map((a) => (
                  <tr key={a.id}
                      data-testid={`staff-row-${a.id}`}
                      className="border-b border-canvas-line hover:bg-canvas-panel/40">
                    <td className="px-4 py-3 font-semibold text-ink">{a.name}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{a.code || "—"}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{a.phone || "—"}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{a.email || "—"}</td>
                    <td className="px-4 py-3 text-sm text-ink-muted">
                      {a.branch_id ? branchById[a.branch_id]?.name || "—" : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${
                        a.active
                          ? "border-[#00B341] bg-[#00B341]/10 text-[#00B341]"
                          : "border-canvas-line text-ink-subtle"
                      }`}>
                        {a.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => { setEditing(a); setDrawerOpen(true); }}
                          data-testid={`edit-staff-${a.id}`}
                          className="p-1.5 border border-canvas-line hover:border-ink"
                        >
                          <PencilSimple size={12} weight="bold" />
                        </button>
                        <button
                          onClick={() => onDelete(a)}
                          data-testid={`delete-staff-${a.id}`}
                          className="p-1.5 border border-destructive text-destructive hover:bg-destructive hover:text-white"
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <StaffFormDrawer
        open={drawerOpen}
        editing={editing}
        role={role}
        branches={branches || []}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => { setDrawerOpen(false); load(); }}
      />
    </div>
  );
}
